import type { Middleware } from "@reduxjs/toolkit";
import type { ProposalStoreState } from "work/proposals/_store/proposals-redux-slice";

export const PROPOSAL_LOCAL_STORE_STORAGE_KEY = "attorneyhub.proposals.localStore.v1";

type StoredProposalLocalStoreState = {
  proposals: ProposalStoreState;
  version: 1;
};

type StorageLike = Pick<Storage, "getItem" | "removeItem" | "setItem">;

export function extractProposalLocalStoreState(state: ProposalStoreState): ProposalStoreState {
  const byId: ProposalStoreState["byId"] = {};

  for (const [proposalIdValue, proposalEntry] of Object.entries(state.byId)) {
    if (!proposalEntry) continue;

    const localEntry = extractProposalLocalStoreEntry(proposalEntry);
    if (localEntryHasLocalState(localEntry)) {
      byId[proposalIdValue] = localEntry;
    }
  }

  return {
    byId,
    byQuery: {},
  };
}

export function loadProposalLocalStoreState(storage = getBrowserLocalStorage()): ProposalStoreState | undefined {
  if (!storage) return undefined;

  try {
    const serializedState = storage.getItem(PROPOSAL_LOCAL_STORE_STORAGE_KEY);
    if (!serializedState) return undefined;

    const parsedState = JSON.parse(serializedState) as unknown;
    return parseStoredProposalLocalStoreState(parsedState);
  } catch {
    return undefined;
  }
}

export function saveProposalLocalStoreState(
  state: ProposalStoreState,
  storage = getBrowserLocalStorage(),
): void {
  if (!storage) return;

  const localStoreState = extractProposalLocalStoreState(state);

  try {
    if (Object.keys(localStoreState.byId).length === 0) {
      storage.removeItem(PROPOSAL_LOCAL_STORE_STORAGE_KEY);
      return;
    }

    const storedState: StoredProposalLocalStoreState = {
      proposals: localStoreState,
      version: 1,
    };
    storage.setItem(PROPOSAL_LOCAL_STORE_STORAGE_KEY, JSON.stringify(storedState));
  } catch {
    return;
  }
}

export const proposalLocalStorePersistenceMiddleware: Middleware = ({ getState }) => (next) => (action) => {
  const previousProposalState = getProposalStateFromRootState(getState());
  const result = next(action);
  const nextProposalState = getProposalStateFromRootState(getState());

  if (nextProposalState && nextProposalState !== previousProposalState) {
    saveProposalLocalStoreState(nextProposalState);
  }

  return result;
};

function parseStoredProposalLocalStoreState(value: unknown): ProposalStoreState | undefined {
  if (!isRecord(value) || value.version !== 1 || !isProposalStoreState(value.proposals)) {
    return undefined;
  }

  return extractProposalLocalStoreState(value.proposals);
}

function extractProposalLocalStoreEntry(
  proposalEntry: NonNullable<ProposalStoreState["byId"][string]>,
): NonNullable<ProposalStoreState["byId"][string]> {
  const localEntry: NonNullable<ProposalStoreState["byId"][string]> = {};
  const draftBuilder = extractProposalDraftBuilderEntry(proposalEntry.draftBuilder);

  if (draftBuilder) localEntry.draftBuilder = draftBuilder;
  if (proposalEntry.localRevision) localEntry.localRevision = proposalEntry.localRevision;
  if (proposalEntry.localVersion) localEntry.localVersion = proposalEntry.localVersion;

  return localEntry;
}

function extractProposalDraftBuilderEntry(
  draftBuilder: NonNullable<ProposalStoreState["byId"][string]>["draftBuilder"],
): NonNullable<ProposalStoreState["byId"][string]>["draftBuilder"] {
  if (!draftBuilder?.original && !draftBuilder?.updated) return undefined;

  return {
    original: draftBuilder.original,
    updated: draftBuilder.updated,
  };
}

function localEntryHasLocalState(proposalEntry: NonNullable<ProposalStoreState["byId"][string]>): boolean {
  return Boolean(proposalEntry.localRevision || proposalEntry.localVersion || proposalEntry.draftBuilder);
}

function getProposalStateFromRootState(rootState: unknown): ProposalStoreState | undefined {
  if (!isRecord(rootState) || !isProposalStoreState(rootState.proposals)) return undefined;
  return rootState.proposals;
}

function isProposalStoreState(value: unknown): value is ProposalStoreState {
  return isRecord(value) && isRecord(value.byId) && isRecord(value.byQuery);
}

function getBrowserLocalStorage(): StorageLike | undefined {
  try {
    return globalThis.localStorage;
  } catch {
    return undefined;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
